import { makeAutoObservable } from "mobx";
import { message } from "antd";
import { State } from "../../enum/state";
import http from "../../services/http";
import { IResultDto } from "../../models/ResultDto";

export default class AdminLicensesStore {
  data?: IOwnersResponse;

  item?: ILicense;

  lookups?: ILookups;

  history?: ILicenseHistory[];

  isLoading = false;

  state: State = "None";

  note: "None" | "New" = "None";

  historyState: "None" | "Preview" = "None";

  noteState: "None" | "Edit" = "None";

  async updateNote(id: string, values: any) {
    this.isLoading = true;
    try {
      const result = await http.put(`/licenses/owners/${id}/note`, values);
      const value = result.data as IResultDto<IOwner>;
      message.success({ content: value.message });
    } finally {
      this.isLoading = false;
      this.noteState = "None";
    }
  }

  async findAll(values: any) {
    this.isLoading = true;
    try {
      const result = await http.get("/licenses/owners", { params: values });

      this.data = result.data;
    } finally {
      this.isLoading = false;
    }
  }

  async onDelete(id?: string) {
    this.isLoading = true;
    try {
      await http.delete(`/licenses/${id}`);
    } finally {
      this.isLoading = false;
    }
  }

  async findLookups() {
    this.isLoading = true;
    try {
      const result = await http.get("/licenses/create");
      this.lookups = result.data;
    } finally {
      this.isLoading = false;
    }
  }

  async onCreate(values: any) {
    this.isLoading = true;
    try {
      const result = await http.post("/licenses", values);
      const value = result.data as IResultDto<ILicense>;
      message.success({ content: value.message });
    } finally {
      this.isLoading = false;
      this.state = "None";
    }
  }

  async onUpdate(values: any) {
    this.isLoading = true;
    try {
      const result = await http.put(`/licenses/${this.item?.id}`, values);
      const value = result.data as IResultDto<ILicense>;
      message.success({ content: value.message });
    } finally {
      this.isLoading = false;
      this.state = "None";
    }
  }

  async onRead(id?: string) {
    if (!id) return;
    this.isLoading = true;
    try {
      const result = await http.get(`/licenses/${id}`);
      const value = result.data as IResultDto<ILicense>;
      this.item = value.data;
      this.state = "Edit";
    } finally {
      this.isLoading = false;
    }
  }

  async onFindHistory(id?: string) {
    this.isLoading = true;
    try {
      const result = await http.get(`/licenses/history/${id}`);
      this.history = result.data.data;
      this.historyState = "Preview";
    } finally {
      this.isLoading = false;
    }
  }

  constructor() {
    makeAutoObservable(this);
  }
}

interface IOwnersResponse {
  data: IOwner[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
    links: [
      {
        url?: string;
        label: string;
        active: boolean;
      }
    ];
  };
}

interface IOwner {
  id: string;
  email: string;
  profile: {
    id: string;
    firstName: string;
    lastName?: string;
    avatar: string;
    phoneNumber: string;
  };
  license?: ILicense;
  activeFeatures: { code: string; title: string }[];
  role: {
    name: string;
    label: string;
  };
  dealsCount: number;
  promocodes: [
    {
      id: string;
      name: string;
      code: string;
      description: string;
      isActive: boolean;
      createdAt: Date;
    }
  ];
  createdAt: Date;
  coupon?: {
    id: string;
    value: string;
  };
  note: string;
}

interface ILicenseHistory {
  id: string;
  name: string;
  action: {
    value: string;
    title: string;
  };
  policiesList: string[];
  expiresAt: Date;
  createdAt: Date;
}

interface ILicense {
  id: string;
  tariff: ITariff;
  limits: ILimitRecord[];
  startsAt: Date;
  expiresAt: Date;
  createdAt: Date;
  description?: string;
}

interface ILookups {
  tariffs: ITariff[];
  limits: ILimit[];
}

interface ITariff {
  id: string;
  name: string;
  limits: ILimitRecord[];
}

interface ILimit {
  value: string;
  title: string;
}

interface ILimitRecord {
  id: string;
  key: ILimit;
  value: number;
}
